/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './ClubRegistrationDisclaimerValidation';

import NavLinks from '../NavLinks';
import ClubRegistrationConstants from '../utils/ClubRegistrationConstants';

import useClubRegistrationNavData from '../../../state/clubRegistration/leftNavigation/UseClubRegistrationNavData';
import useClubRegistrationNewData from '../../../state/clubRegistrationNew/UseClubRegistrationNewData';
import useClubRegistrationPaymentData from '../../../state/clubRegistration/payment/UseClubRegistrationPaymentData';
import useClubOwnerTypeData from '../../../state/clubOwnerType/UseClubOwnerTypeData';
import useBusinessEntityData from '../../../state/businessEntity/UseBusinessEntityData';
import useClubRegistrationRequirmentData from '../../../state/clubRegistration/requirements/UseClubRegistrationRequirementData';
import useLscMapData from '../../../state/clubRegistration/lscMaps/UseLscMapData';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';

const useClubRegistrationDisclaimer = () => {
  const navigate = useNavigate();
  const { clubRegRequirementState, getClubRegistrationRequirment } = useClubRegistrationRequirmentData();
  const { clubRegistrationNavState, setClubRegistrationNavState, resetClubRegistrationNavState } = useClubRegistrationNavData();
  const { clubRegistrationState, setClubRegistrationState, putUsasClubAndHelperObj, clearObjData } = useClubRegistrationNewData();
  const { lscMapState, getLscMaps} = useLscMapData();
  const { clearClubRegistrationPaymentState } = useClubRegistrationPaymentData();
  const { clubOwnerTypeState } = useClubOwnerTypeData();
  const { businessEntityState } = useBusinessEntityData();
  const { environmentVariableState, BOARD_OWNED_ORG_LEVEL_TYPE_ID } = useEnvironmentVariableData();
  const [state, setState] = useState({ showNumOfBoardMembers: false, isInitialPageLoad: true, tryRedirect: false });
  const [ filteredClubRegRequirementState, setFilteredClubRegRequirementState ] = useState([]);
  const [ selectedLscMapState, setSelectedLscMapState ] = useState({ selectedLscMap: '' });

  const { handleSubmit, updateFormState, formState, setFormState, errorState, onValueTextPairChanged, setIsDirty
  } = useForm(getFormInitialState(), submitFormCallback, validate);

  function submitFormCallback() {
    let clubCopy = JSON.parse(JSON.stringify(clubRegistrationState.objData));
    //Initial Add
    if (Object.keys(clubCopy).length < 3) {
      setClubRegistrationState({
        ...clubRegistrationState,
        objData: {
          ...clubRegistrationState.objData,
          clubOwnerTypeId: formState.clubOwnerTypeId,
          businessEntityTypeId: formState.businessEntityTypeId,
          lscId: formState.lscId,
          offeringInstanceId: formState.offeringInstanceId
        },
        helperObj: {
          ...clubRegistrationState.helperObj,
          ownerTypeName: formState.ownerTypeName,
          businessEntityTypeName: formState.businessEntityTypeName,
          numOfBoardMembers: formState.numOfBoardMembers,
          lscName: formState.lscName
        }
      });
      onContinueClicked();
    }
    //Edit (on returning to page)
    else if (Object.keys(clubCopy).length > 2) {
      if (clubRegistrationState.objData.status === ClubRegistrationConstants.IN_PROGRESS_STATUS
        // || clubRegistrationState.objData.status === ClubRegistrationConstants.RENEWING_STATUS
      ) {
        //set staff nav state to incomplete on changes to ownerType
        const selectedClubOwnerType = clubOwnerTypeState.data.find(e => e.clubOwnerTypeId === formState.clubOwnerTypeId);
        if (selectedClubOwnerType.orgLevelType?.orgLevelTypeId !== clubRegistrationState.objData.ownerType?.orgLevelTypeId) {
          const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_STAFF)
          clubRegistrationNavState[navIndex] =
          {
            ...clubRegistrationNavState[navIndex],
            isComplete: false
          }
          setClubRegistrationNavState(clubRegistrationNavState);
        }

        //save any changes
        if (formState.clubOwnerTypeId !== clubRegistrationState.objData.clubOwnerTypeId ||
          formState.businessEntityTypeId !== clubRegistrationState.objData.businessEntityTypeId) {
          const helperObj = {
            ...clubRegistrationState.helperObj,
            ownerTypeName: formState.ownerTypeName,
            businessEntityTypeName: formState.businessEntityTypeName,
            numOfBoardMembers: formState.numOfBoardMembers,
            lscName: formState.lscName
          }
          
          putUsasClubAndHelperObj(clubCopy.usasClubId, editClubObj(clubCopy), helperObj);
          setState({ ...state, tryRedirect: true });
        }
        else {
          onContinueClicked();
        }
      }
    }
  }

  const onContinueClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(NavLinks.CLUB_REGISTRATION_INFO);
  }

  const onBackClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    resetClubRegistrationNavState();
    clearObjData();
    clearClubRegistrationPaymentState();
    navigate(NavLinks.CLUB_REGISTRATION_ENTRY_NEW);
  }

  const editClubObj = (clubCopy) => {
    clubCopy = {
      ...clubRegistrationState.objData,
      businessEntityTypeId: formState.businessEntityTypeId,
      clubOwnerTypeId: formState.clubOwnerTypeId,
      businessEntityType: undefined,
      ownerType: undefined
    }
    return clubCopy;
  }

  useEffect(() => {
    //If navigating back to this page, set state to any previous data values
    if (Object.keys(clubRegistrationState.objData).length > 0) {
      setState({
        ...state,
        showNumOfBoardMembers: clubRegistrationState.helperObj.numOfBoardMembers === '' ? false : true
      });
      if (Object.keys(clubRegistrationState.helperObj).length > 0) {
        setFormState({
          ...formState,
          clubOwnerTypeId: clubRegistrationState.objData.clubOwnerTypeId || Constants.DEFAULT_ID,
          ownerTypeName: clubRegistrationState.helperObj.ownerTypeName || '',
          businessEntityTypeId: clubRegistrationState.objData.businessEntityTypeId || Constants.DEFAULT_ID,
          businessEntityTypeName: clubRegistrationState.helperObj.businessEntityTypeName || '',
          numOfBoardMembers: clubRegistrationState.helperObj.numOfBoardMembers || '',
          isNumOfBoardMembersRequired: clubRegistrationState.helperObj.numOfBoardMembers === '' ? false : true,
          lscName: clubRegistrationState.helperObj.lscName || ''
        });
      }
      else {
        setFormState({
          ...formState,
          clubOwnerTypeId: clubRegistrationState.objData.clubOwnerTypeId || Constants.DEFAULT_ID,
          businessEntityTypeId: clubRegistrationState.objData.businessEntityTypeId || Constants.DEFAULT_ID
        });
      }
      setIsDirty(true);
    }

    const getClubRegistrationRequirmentPromise = getClubRegistrationRequirment();

    if (getClubRegistrationRequirmentPromise !== null) {
      getClubRegistrationRequirmentPromise.then((newState) => {
        if (newState !== null) {
          //nothing to do here
        }
      }).catch((e) => {
        //TODO state error
      });
    }

    const getLscMapsPromise = getLscMaps();

    if (getLscMapsPromise !== null) {
      getLscMapsPromise.then((newState) => {  
        if (newState !== null) {
          //nothing to do here
        } 
      }).catch((e) => {
        //TODO state error
      });
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (formState.clubOwnerTypeId === clubRegistrationState.objData.clubOwnerTypeId &&
      clubOwnerTypeState.isLoaded === true && clubOwnerTypeState.isLoading === false &&
      businessEntityState.isArrayLoading === false && businessEntityState.clubOwnerTypeId === formState.clubOwnerTypeId
      && formState.businessEntityTypeId > Constants.DEFAULT_ID && state.isInitialPageLoad === true) {
      //If navigating back to this page, handle loading delay for setting the tax listing from previous data
      if (Object.keys(clubRegistrationState.objData).length > 0) {
        setState({ ...state, isInitialPageLoad: false })
        if (Object.keys(clubRegistrationState.helperObj).length > 0) {
          setFormState({
            ...formState,
            businessEntityTypeId: clubRegistrationState.objData.businessEntityTypeId,
            businessEntityTypeName: clubRegistrationState.helperObj.businessEntityTypeName || '',
          });
        }
        else {
          setFormState({
            ...formState,
            businessEntityTypeId: clubRegistrationState.objData.businessEntityTypeId
          });
        }
      }
      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubOwnerTypeState, businessEntityState, formState.businessEntityTypeId])

  useEffect(() => {
    //Handle changes to club owner type
    if (environmentVariableState.isLoaded === true && clubOwnerTypeState.isLoaded === true
      && clubOwnerTypeState.isLoading === false && formState.clubOwnerTypeId > 0) {
      const selectedClubOwnerType = clubOwnerTypeState.data.find(e => e.clubOwnerTypeId === formState.clubOwnerTypeId);
      if (selectedClubOwnerType.orgLevelType?.orgLevelTypeId === BOARD_OWNED_ORG_LEVEL_TYPE_ID) {
        setState({ ...state, showNumOfBoardMembers: true })
        setFormState({ ...formState, isNumOfBoardMembersRequired: true });
      }
      else {
        setState({ ...state, showNumOfBoardMembers: false })
        setFormState({ ...formState, numOfBoardMembers: '', isNumOfBoardMembersRequired: false });
      }
      setIsDirty(true);

      const filteredArray = clubRegRequirementState.objData.filter(x => x.orgLevelTypeId === selectedClubOwnerType.orgLevelType?.orgLevelTypeId);
      setFilteredClubRegRequirementState(filteredArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentVariableState, clubOwnerTypeState, clubRegRequirementState, formState.clubOwnerTypeId])

  useEffect(() => {
    if (lscMapState.isObjLoaded === true && lscMapState.isSaving === false && formState.lscId !== Constants.DEFAULT_ID) {
      if (lscMapState.objData.find(x => x.orgUnitId === formState.lscId)) {
        setSelectedLscMapState({ selectedLscMap: `LSCMaps/${lscMapState.objData.find(x => x.orgUnitId === formState.lscId).orgUnitCode}Map.png` });
      } else {
        setSelectedLscMapState({ selectedLscMap: '' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lscMapState, formState.lscId])

  useEffect(() => {
    if (state.tryRedirect === true && clubRegistrationState.isObjLoaded === true && clubRegistrationState.isSaving === false) {
      onContinueClicked();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryRedirect, clubRegistrationState])

  useEffect(() => {
    if (formState.clubOwnerTypeId === Constants.DEFAULT_ID) {
      setFormState({
        ...formState,
        isAllRequiredInfoReady: false,
        numOfBoardMembers: ''
      });
      setSelectedLscMapState({ selectedLscMap: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.clubOwnerTypeId])

  function getFormInitialState() {
    return {
      clubOwnerTypeId: Constants.DEFAULT_ID,
      ownerTypeName: '',
      businessEntityTypeId: Constants.DEFAULT_ID,
      businessEntityTypeName: '',
      numOfBoardMembers: '',
      isNumOfBoardMembersRequired: false,
      isAcknowledged: false,
      lscId: Constants.DEFAULT_ID,
      lscName: '',
      isCorrectLscSelected: false,
      offeringInstanceId: Constants.DEFAULT_ID,
      isAllRequiredInfoReady: false
    };
  }

  return {
    state,
    clubRegistrationState,
    clubOwnerTypeState,
    businessEntityState,
    formState,
    errorState,
    filteredClubRegRequirementState,
    selectedLscMapState,
    onFormValueChanged: updateFormState,
    onValueTextPairChanged,
    handleSubmit,
    onContinueClicked,
    onBackClicked
  }
}

export default useClubRegistrationDisclaimer;
